import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { portuguese } from "../translations";

export type Language = "en" | "pt";
export type TranslationValues = Record<string, string | number>;
export const LANGUAGE_KEY = "sayitsorted.language";

export function detectLanguage(
  saved: string | null,
  languages: readonly string[],
): Language {
  if (saved === "en" || saved === "pt") return saved;
  for (const language of languages) {
    const base = language.toLowerCase().split(/[-_]/)[0];
    if (base === "en" || base === "pt") return base;
  }
  return "en";
}

function initialLanguage(): Language {
  let saved = null;
  try {
    saved = localStorage.getItem(LANGUAGE_KEY);
  } catch {
    /* Selection still works without storage. */
  }
  return detectLanguage(
    saved,
    navigator.languages?.length ? navigator.languages : [navigator.language],
  );
}

export function translate(
  language: Language,
  key: string,
  values: TranslationValues = {},
): string {
  const text = language === "pt" ? (portuguese[key] ?? key) : key;
  return text.replace(/\{(\w+)\}/g, (match, name) =>
    values[name] === undefined ? match : String(values[name]),
  );
}

interface LanguageContextValue {
  language: Language;
  locale: string;
  setLanguage: (language: Language) => void;
  t: (key: string, values?: TranslationValues) => string;
  money: (amount: number, currency: string) => string;
  formatDate: (date: string | null) => string;
  planTitle: (title: string) => string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(
  undefined,
);

export function LanguageProvider({
  children,
  preferredLanguage,
}: {
  children: React.ReactNode;
  preferredLanguage?: Language;
}) {
  const [language, updateLanguage] = useState<Language>(
    () => preferredLanguage ?? initialLanguage(),
  );
  const locale = language === "pt" ? "pt-PT" : "en-GB";
  const t = useCallback(
    (key: string, values?: TranslationValues) =>
      translate(language, key, values),
    [language],
  );
  const setLanguage = (next: Language) => {
    updateLanguage(next);
    try {
      localStorage.setItem(LANGUAGE_KEY, next);
    } catch {
      /* In-memory preference remains usable. */
    }
  };
  useEffect(() => {
    document.documentElement.lang = locale;
    document.title = t("Say it sorted | AI Travel Planning for Remote Workers");
    const description = t(
      "A new city. A better routine. Tell Say it sorted what you have in mind and explore flights, stays, and a clear travel brief for your next chapter.",
    );
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", description);
    document
      .querySelector('meta[property="og:description"]')
      ?.setAttribute("content", description);
    document
      .querySelector('meta[property="og:title"]')
      ?.setAttribute("content", document.title);
    document
      .querySelector('meta[name="twitter:title"]')
      ?.setAttribute("content", document.title);
    document
      .querySelector('meta[name="twitter:description"]')
      ?.setAttribute("content", description);
    document
      .querySelector('meta[property="og:locale"]')
      ?.setAttribute("content", locale.replace("-", "_"));
    document
      .querySelector('meta[property="og:locale:alternate"]')
      ?.setAttribute("content", locale === "pt-PT" ? "en_GB" : "pt_PT");
  }, [locale, t]);
  useEffect(() => {
    const sync = (event: StorageEvent) => {
      if (
        event.key === LANGUAGE_KEY &&
        (event.newValue === "en" || event.newValue === "pt")
      )
        updateLanguage(event.newValue);
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  return (
    <LanguageContext.Provider
      value={{
        language,
        locale,
        setLanguage,
        t,
        money: (amount, currency) =>
          new Intl.NumberFormat(locale, { style: "currency", currency }).format(
            amount,
          ),
        formatDate: (value) =>
          value
            ? new Intl.DateTimeFormat(locale, {
                day: "numeric",
                month: "short",
                year: "numeric",
                timeZone: "UTC",
              }).format(new Date(`${value}T12:00:00Z`))
            : t("Dates open"),
        planTitle: (title) =>
          ["Your next chapter", "New Chat"].includes(title) ? t(title) : title,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context)
    throw new Error("useLanguage must be used within LanguageProvider");
  return context;
}
